import React, { Fragment, useEffect, useRef, useState } from 'react'
import { Row, Col, Dropdown, Button, Image, Collapse } from 'react-bootstrap'
import Flatpickr from 'react-flatpickr'
import debounce from 'lodash/debounce'
import { gql, useQuery, useLazyQuery } from '@apollo/client'
import { InView } from 'react-intersection-observer'
import ScrollToTop from 'react-scroll-to-top'
import { ToastContainer } from 'react-toastify'
import Card from '../components/common/Card'
import CustomToggle from '../layouts/components/CustomToggle'
import { logos } from '../utils/platform-logos'
import FeedCard from '../components/feed/FeedCard'
import Masonry from '../components/common/Masonry'
import FilterOptions from '../components/feed/FilterOption'
import ResponsiveMasonry from '../components/common/ResponsiveMasonry'
import { capitaliseFirstLetter } from '../utils/tools'
import { CORE_FEED_FIELDS } from '../graphql/feed-fragments'
import { showNotification } from '../utils/notifications'
import { CubeLoader } from '../layouts/components/CubeLoader'
import { CalendarIcon, ChevronDownIcon, ChevronUpIcon, UsersIcon } from '../components/icons'

const GET_PLATFORMS = gql`
    query Platforms($options: PlatformOptions, $where: PlatformWhere) {
        platforms(options: $options, where: $where) {
            id
            name
            type
        }
    }
`

const GET_FEEDS = gql`
    ${CORE_FEED_FIELDS}
    query Feeds($where: FeedWhere, $options: FeedOptions) {
        feeds(where: $where, options: $options) {
            ...CoreFeedFields
            comments {
                ...CoreFeedFields
            }
            keywords {
                id
                value
            }
        }
    }
`

const GET_CAMPAIGN = gql`
    query Campaigns {
        campaigns {
            keywords
        }
    }
`

const SENTIMENT_OPTIONS = {
    POSITIVE: 'positive',
    NEUTRAL: 'neutral',
    NEGATIVE: 'negative',
}

const SocialFeed = () => {
    const [platform, setPlatform] = useState(null)
    const [heights, setHeights] = useState({})
    const [isFullyLoaded, setFullyLoaded] = useState(false)
    const [isOpenFilters, setIsOpenFilters] = useState(false)
    const [keywords, setKeywords] = useState([])
    const [selectedKeywords, setSelectedKeywords] = useState([])
    const [sentiments, setSentiments] = useState([])
    const [filters, setFilters] = useState([])
    const [dateRange, setDateRange] = useState({ from: null, to: null })
    const [deleting, setDeleting] = useState(null)
    const datePickerRef = useRef(null)

    const campaign = useQuery(GET_CAMPAIGN)

    useEffect(() => {
        if (!campaign.loading && campaign?.data?.campaigns) {
            if (campaign.data.campaigns[0].keywords !== null) {
                setKeywords(JSON.parse(campaign.data.campaigns[0].keywords))
            }
        }
    }, [campaign])

    const platforms = useQuery(GET_PLATFORMS, {
        variables: {
            options: { sort: [{ order: 'ASC' }] },
            where: { status: 'ACTIVE' },
        },
        onCompleted: () => {
            setPlatform(platforms.data.platforms[0])
            fetchFeeds({
                variables: {
                    where: {
                        platform: { id: platforms.data.platforms[0].id },
                        type: null,
                    },
                    options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
                },
            })
        },
    })

    const [fetchFeeds, { data: feedsData, loading: feedsLoading, fetchMore }] = useLazyQuery(GET_FEEDS)

    const onChangePlatform = (platform) => {
        setPlatform(platform)
        applyFilters(platform, dateRange, sentiments, selectedKeywords)
    }

    const onLoadMore = async () => {
        const result = await fetchMore({
            variables: {
                options: {
                    sort: [{ postedAt: 'DESC' }],
                    limit: 18,
                    offset: feedsData?.feeds.length,
                },
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                if (!fetchMoreResult) return prev
                const previousFeeds = prev?.feeds ?? []
                const newFeeds = fetchMoreResult.feeds ?? []

                return {
                    ...prev,
                    feeds: [...previousFeeds, ...newFeeds],
                }
            },
        })
        setFullyLoaded(!result.data.feeds.length)
    }

    const onDeleteFeed = async (id) => {
        setDeleting(id)
        await fetchMore({
            variables: {
                options: {
                    sort: [{ postedAt: 'DESC' }],
                    limit: 0,
                    offset: feedsData?.feeds.length,
                },
            },
            updateQuery: (prev, { fetchMoreResult }) => {
                const filteredFeeds = prev.feeds.filter((feed) => feed.id !== id)
                return {
                    ...prev,
                    feeds: filteredFeeds,
                }
            },
        })
        setTimeout(() => showNotification('The Feed has been deleted', 'success'), 10)
        setDeleting(null)
    }

    const handleHeightChange = (index, height) => {
        setHeights((prevState) => {
            return {
                ...prevState,
                [index]: height,
            }
        })
    }

    const debouncedApplyFilters = debounce(async (platform, dateRange, sentiments, selectedKeywords) => {
        const where = {
            platform: { id: platform?.id },
            type: null,
        }

        if (dateRange.from) {
            where.postedAt_GTE = dateRange.from.toISOString()
        }

        if (dateRange.to) {
            where.postedAt_LTE = dateRange.to.toISOString()
        }

        if (sentiments.length > 0) {
            where.sentiment_IN = sentiments
        }

        if (selectedKeywords.length > 0) {
            where.keywords_SOME = { value_IN: selectedKeywords }
        }

        setFullyLoaded(false)
        const result = await fetchFeeds({
            variables: {
                where,
                options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
            },
        })
        setFullyLoaded(!result.data.feeds.length)
    }, 300)

    const applyFilters = (platform, dateRange, sentiments, selectedKeywords) => {
        debouncedApplyFilters(platform, dateRange, sentiments, selectedKeywords)
    }

    const handleFilterChange = (filter, checked, filterType) => {
        setFilters((prevFilters) => {
            let updatedFilters
            if (checked) {
                updatedFilters = [...prevFilters, filter]
            } else {
                updatedFilters = prevFilters.filter((f) => f !== filter)
            }

            if (filterType === 'keyword') {
                setSelectedKeywords((prevKeywords) => {
                    const newKeywords = checked ? [...prevKeywords, filter] : prevKeywords.filter((k) => k !== filter)
                    applyFilters(platform, dateRange, sentiments, newKeywords)
                    return newKeywords
                })
            } else if (filterType === 'sentiment') {
                setSentiments((prevSentiments) => {
                    const sentimentValue =
                        filter === SENTIMENT_OPTIONS.POSITIVE ? 1 : filter === SENTIMENT_OPTIONS.NEUTRAL ? 0 : -1
                    const newSentiments = checked
                        ? [...prevSentiments, sentimentValue]
                        : prevSentiments.filter((s) => s !== sentimentValue)
                    applyFilters(platform, dateRange, newSentiments, selectedKeywords)
                    return newSentiments
                })
            }

            // Update date range state if date filter is removed
            if (filter.startsWith('From:')) {
                setDateRange((prevDateRange) => {
                    const newDateRange = { ...prevDateRange, from: null }
                    applyFilters(platform, newDateRange, sentiments, selectedKeywords)
                    return newDateRange
                })
            } else if (filter.startsWith('To:')) {
                setDateRange((prevDateRange) => {
                    const newDateRange = { ...prevDateRange, to: null }
                    applyFilters(platform, newDateRange, sentiments, selectedKeywords)
                    return newDateRange
                })
            }

            return updatedFilters
        })
    }

    const handleDateRangeChange = (selectedDates) => {
        const from = selectedDates[0] || null
        const to = selectedDates[1] || null
        const newDateRange = { from, to }
        const fromFilter = from ? `From: ${from.toLocaleDateString()}` : null
        const toFilter = to ? `To: ${to.toLocaleDateString()}` : null

        setFilters((prevFilters) => {
            let updatedFilters = prevFilters.filter(
                (filter) => !filter.startsWith('From: ') && !filter.startsWith('To: '),
            )
            if (fromFilter) updatedFilters.push(fromFilter)
            if (toFilter) updatedFilters.push(toFilter)
            return updatedFilters
        })

        setDateRange(newDateRange)
        applyFilters(platform, newDateRange, sentiments, selectedKeywords)
    }

    const clearAllFilters = async () => {
        setFullyLoaded(false)
        setFilters([])
        setDateRange({ from: null, to: null })
        setSelectedKeywords([])
        setSentiments([])
        const result = await fetchFeeds({
            variables: {
                where: { platform: { id: platform?.id }, type: null },
                options: { sort: [{ postedAt: 'DESC' }], limit: 18, offset: 0 },
            },
        })
        setFullyLoaded(!result.data.feeds.length)
    }

    return (
        <Fragment>
            {platforms.loading ? (
                <CubeLoader fullScreen={true} />
            ) : (
                <Row className="mb-4">
                    <Col lg="12">
                        <Card className="page-card">
                            <Card.Header className="align-items-center flex-wrap page-card-header gap-2">
                                <h5 className="mb-0 d-flex align-items-center gap-2">
                                    <i className="icon text-gray-200">
                                        <UsersIcon size={24} color="currentColor" />
                                    </i>
                                    {capitaliseFirstLetter(platform?.type)} Feed{' '}
                                    <span className="hide-on-mobile">- {platform?.name} </span>
                                    <Button variant="sm social-btn bg-white rounded-circle p-1 no-pointer">
                                        <Image
                                            src={logos[platform?.name]}
                                            alt={platform?.name}
                                            className="img-fluid object-contain"
                                            style={{ width: '20px', height: '20px' }}
                                        />
                                    </Button>
                                </h5>
                                <div className="d-flex flex-row gap-2">
                                    <div
                                        className={`text-secondary dropdown-toggle ${isOpenFilters ? 'show' : ''}`}
                                        onClick={() => setIsOpenFilters(!isOpenFilters)}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        <span className="text-sm text-blue-300 mb-0 d-flex align-items-center gap-1">
                                            Filters
                                            <i className="icon">
                                                {isOpenFilters ? <ChevronUpIcon /> : <ChevronDownIcon />}
                                            </i>
                                        </span>
                                    </div>
                                    <Dropdown>
                                        <Dropdown.Toggle
                                            as={CustomToggle}
                                            variant="text-secondary dropdown-toggle"
                                            style={{ cursor: 'pointer' }}
                                        >
                                            <span className="text-sm text-blue-300 mb-0 d-flex align-items-center gap-1">
                                                Select Platform
                                                <i className="icon">
                                                    <ChevronDownIcon />
                                                </i>
                                            </span>
                                        </Dropdown.Toggle>
                                        <Dropdown.Menu aria-labelledby="dropdownMenuButtonPlatform">
                                            {platforms.data.platforms.map((item) => {
                                                return (
                                                    <li key={item.id}>
                                                        <Dropdown.Item href="#" onClick={() => onChangePlatform(item)}>
                                                            <Button
                                                                variant="sm social-btn rounded-circle no-pointer p-0 me-1 d-inline-flex align-items-center justify-content-center"
                                                                style={{ width: '20px', height: '20px' }}
                                                            >
                                                                <Image
                                                                    src={logos[item.name]}
                                                                    alt={item.name}
                                                                    className="img-fluid object-contain"
                                                                    style={{ width: '14px', height: '14px' }}
                                                                />
                                                            </Button>{' '}
                                                            {item.name}
                                                        </Dropdown.Item>
                                                    </li>
                                                )
                                            })}
                                        </Dropdown.Menu>
                                    </Dropdown>
                                    {/* move date range selector to the right */}
                                    <div>
                                        <i
                                            className="icon text-blue-500 d-flex align-items-center gap-1"
                                            style={{ cursor: 'pointer' }}
                                            onClick={() => datePickerRef.current?.flatpickr?.open()}
                                            role="button"
                                            aria-label="Open date range picker"
                                        >
                                            <CalendarIcon />
                                            <span className="text-sm text-blue-300 mb-0">Set Period</span>
                                            <Flatpickr
                                                ref={datePickerRef}
                                                options={{
                                                    mode: 'range',
                                                    maxDate: 'today',
                                                    dateFormat: 'd/m/Y',
                                                }}
                                                className="range_flatpicker hidden-input"
                                                value={[dateRange.from, dateRange.to].filter(Boolean)}
                                                onChange={handleDateRangeChange}
                                                placeholder="Select date range"
                                            />
                                        </i>
                                    </div>
                                </div>
                            </Card.Header>
                            <Card.Body className="page-card-body">
                                <Collapse in={isOpenFilters}>
                                    <Row className="mb-4">
                                        <Col md={4}>
                                            <h6 className="mb-2">Keywords</h6>
                                            <div>
                                                {keywords.map((keyword, index) => (
                                                    <FilterOptions
                                                        key={index}
                                                        uniqueName="keyword"
                                                        id={index.toString()}
                                                        productName={keyword}
                                                        productChecked={selectedKeywords.includes(keyword)}
                                                        onChange={(e) =>
                                                            handleFilterChange(keyword, e.target.checked, 'keyword')
                                                        }
                                                    />
                                                ))}
                                            </div>
                                        </Col>

                                        <Col md={4}>
                                            <h6 className="mb-2">Sentiment</h6>
                                            <div>
                                                <div id="iq-product-filter-03" className="filter-collapse">
                                                    <FilterOptions
                                                        uniqueName="sentiment"
                                                        id="positive"
                                                        productName={SENTIMENT_OPTIONS.POSITIVE}
                                                        productChecked={filters.includes(SENTIMENT_OPTIONS.POSITIVE)}
                                                        onChange={(e) =>
                                                            handleFilterChange(
                                                                SENTIMENT_OPTIONS.POSITIVE,
                                                                e.target.checked,
                                                                'sentiment',
                                                            )
                                                        }
                                                    />
                                                    <FilterOptions
                                                        uniqueName="sentiment"
                                                        id="neutral"
                                                        productName={SENTIMENT_OPTIONS.NEUTRAL}
                                                        productChecked={filters.includes(SENTIMENT_OPTIONS.NEUTRAL)}
                                                        onChange={(e) =>
                                                            handleFilterChange(
                                                                SENTIMENT_OPTIONS.NEUTRAL,
                                                                e.target.checked,
                                                                'sentiment',
                                                            )
                                                        }
                                                    />
                                                    <FilterOptions
                                                        uniqueName="sentiment"
                                                        id="negative"
                                                        productName={SENTIMENT_OPTIONS.NEGATIVE}
                                                        productChecked={filters.includes(SENTIMENT_OPTIONS.NEGATIVE)}
                                                        onChange={(e) =>
                                                            handleFilterChange(
                                                                SENTIMENT_OPTIONS.NEGATIVE,
                                                                e.target.checked,
                                                                'sentiment',
                                                            )
                                                        }
                                                    />
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </Collapse>
                                <div className="align-items-center w-100">
                                    {filters.map((filter, index) => (
                                        <div key={index} className="bg-soft-primary rounded-pill iq-custom-badge mb-3">
                                            {filter}
                                            <Button
                                                className="btn btn-primary btn-sm rounded-pill iq-cancel-btn"
                                                onClick={() =>
                                                    handleFilterChange(
                                                        filter,
                                                        false,
                                                        filter.includes(SENTIMENT_OPTIONS.POSITIVE) ||
                                                            filter.includes(SENTIMENT_OPTIONS.NEUTRAL) ||
                                                            filter.includes(SENTIMENT_OPTIONS.NEGATIVE)
                                                            ? 'sentiment'
                                                            : 'keyword',
                                                    )
                                                }
                                            >
                                                <svg
                                                    width="14"
                                                    height="14"
                                                    xmlns="http://www.w3.org/2000/svg"
                                                    fill="none"
                                                    viewBox="0 0 24 24"
                                                    stroke="currentColor"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth="2"
                                                        d="M6 18L18 6M6 6l12 12"
                                                    ></path>
                                                </svg>
                                            </Button>
                                        </div>
                                    ))}
                                    {filters.length > 1 && (
                                        <Button className="ml-auto btn btn-primary btn-sm" onClick={clearAllFilters}>
                                            Clear All
                                        </Button>
                                    )}
                                </div>

                                <Row>
                                    {!feedsLoading && (
                                        <ResponsiveMasonry columnsCountBreakPoints={{ 350: 1, 900: 3, 1200: 4 }}>
                                            <Masonry gutter={'20px'} heights={heights}>
                                                {feedsData?.feeds.map((feed, index) => {
                                                    const uniqueKey = `${feed.id}-${index}`
                                                    return (
                                                        <FeedCard
                                                            key={uniqueKey}
                                                            data={feed}
                                                            platform={platform}
                                                            index={uniqueKey}
                                                            onHeightChange={handleHeightChange}
                                                            onDelete={onDeleteFeed}
                                                            onDeleting={deleting}
                                                        />
                                                    )
                                                })}
                                                <InView
                                                    onChange={(inView) => {
                                                        if (inView) {
                                                            onLoadMore()
                                                        }
                                                    }}
                                                />
                                            </Masonry>
                                        </ResponsiveMasonry>
                                    )}
                                    {!isFullyLoaded && <CubeLoader fullScreen={true} />}
                                </Row>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            )}
            <ScrollToTop smooth color="white" className="turbamedia-fab" />
            <ToastContainer />
        </Fragment>
    )
}

export default SocialFeed
